/**
 * Audit viewer page (T7 — plan §7.2).
 *
 * URL: ``/projects/:projectId/audit?runId=N`` (defaults to run 1).
 *
 * Two columns:
 *   * Left — vertical timeline of ``audit/timeline.jsonl`` rows
 *     grouped by phase. The header shows verify badge + total events
 *     and (when available) the phase duration in seconds.
 *   * Right — side panel with the JSON of the selected entry. Future
 *     work (T7 follow-up) loads ``audit/llm/<id>.json`` for
 *     ``llm_usage`` rows; for now we show the inline payload.
 *
 * The page is intentionally read-only — every mutation lives on the
 * Command Center route. The audit viewer's whole job is to surface
 * what the AuditWriter (T4) and HitlStore (history.log) persisted.
 */
import { useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useAuditStore, type LLMCallEntry } from '@/stores/auditStore'
import { lineDiff, summariseDiff, type DiffOp } from '@/lib/diff'
import {
  groupByPhase,
  type AuditEvent,
  type AuditPhaseGroup,
  type LLMUsagePayload,
  type RewriterProposedPayload,
} from '@/types/audit'

const DEFAULT_RUN_ID = 1

export function AuditPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [searchParams] = useSearchParams()
  const runIdParam = searchParams.get('runId')
  const runId = runIdParam ? Number(runIdParam) : DEFAULT_RUN_ID

  const entries = useAuditStore((s) => s.entries)
  const loading = useAuditStore((s) => s.loading)
  const error = useAuditStore((s) => s.error)
  const selectedTs = useAuditStore((s) => s.selectedTs)
  const hydrate = useAuditStore((s) => s.hydrate)
  const reset = useAuditStore((s) => s.reset)
  const selectEntry = useAuditStore((s) => s.selectEntry)

  useEffect(() => {
    if (!projectId) return
    void hydrate(projectId, runId)
    return () => {
      reset()
    }
  }, [projectId, runId, hydrate, reset])

  const groups = useMemo(() => groupByPhase(entries), [entries])
  const selected = useMemo(
    () => entries.find((e) => e.ts === selectedTs) ?? null,
    [entries, selectedTs],
  )

  return (
    <div
      className="min-h-[calc(100vh-96px)] text-slate-100"
      data-testid="audit-page"
    >
      <div className="max-w-6xl mx-auto px-6 py-6">
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              Audit · Project {projectId} · Run {runId}
            </div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Audit trail</h1>
          </div>
          <VerifyBadge />
        </header>

        {error ? (
          <div
            data-testid="audit-error"
            className="rounded-md border border-red-700/50 bg-red-950/40 p-3 text-sm text-red-200 mb-4"
          >
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-6">
          <section
            data-testid="audit-timeline"
            className="col-span-2 rounded-2xl border border-slate-700/60 bg-slate-900/60 p-5"
          >
            {loading && entries.length === 0 ? (
              <div className="text-sm text-slate-400">Streaming timeline…</div>
            ) : entries.length === 0 ? (
              <div
                data-testid="audit-empty"
                className="text-sm text-slate-400"
              >
                No audit events recorded for this run yet. The trail
                appears once a phase fires.
              </div>
            ) : (
              groups.map((group) => (
                <PhaseGroupView
                  key={group.phase ?? 'unscoped'}
                  group={group}
                  selectedTs={selectedTs}
                  onSelect={selectEntry}
                />
              ))
            )}
          </section>

          <aside
            data-testid="audit-detail"
            className="col-span-1 rounded-2xl border border-slate-700/60 bg-slate-900/60 p-5"
          >
            {selected ? (
              <SelectedEntry event={selected} />
            ) : (
              <div className="text-sm text-slate-400">
                Click a row to inspect the raw event payload.
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  )
}

function LLMCallDetail({ payload }: { payload: LLMUsagePayload }) {
  const callPath = payload.call_path
  const llmCalls = useAuditStore((s) => s.llmCalls)
  const fetchLLMCall = useAuditStore((s) => s.fetchLLMCall)

  useEffect(() => {
    if (!callPath) return
    void fetchLLMCall(callPath)
  }, [callPath, fetchLLMCall])

  if (!callPath) {
    return (
      <div
        data-testid="audit-llm-no-path"
        className="text-[11px] text-slate-500 mt-2"
      >
        no call_path on this row — the producer may be running with
        ``ai_alm_test_audit_writer`` set to false.
      </div>
    )
  }

  const entry: LLMCallEntry | undefined = llmCalls[callPath]
  if (!entry || entry.status === 'loading') {
    return (
      <div
        data-testid="audit-llm-loading"
        className="text-[11px] text-slate-500 mt-2"
      >
        loading {callPath}…
      </div>
    )
  }
  if (entry.status === 'error' || !entry.body) {
    return (
      <div
        data-testid="audit-llm-error"
        className="text-[11px] text-red-300 mt-2"
      >
        could not load {callPath}: {entry.error ?? 'unknown error'}
      </div>
    )
  }

  const body = entry.body
  return (
    <div data-testid="audit-llm-detail" className="mt-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        LLM call · {body.agent}
      </div>
      <div className="text-[11px] text-slate-400 mb-2">
        {body.response.model} · in {body.response.input_tokens} ↘
        out {body.response.output_tokens} ↗ · {body.response.duration_ms} ms
      </div>
      <details
        data-testid="audit-llm-system"
        className="mb-2"
      >
        <summary className="cursor-pointer text-[11px] font-semibold text-indigo-300">
          System prompt
        </summary>
        <pre className="bg-slate-950/60 border border-slate-800 rounded-md p-2 mt-1 overflow-x-auto whitespace-pre-wrap text-[11px] leading-relaxed">
          {body.request.system}
        </pre>
      </details>
      <details
        data-testid="audit-llm-messages"
        className="mb-2"
      >
        <summary className="cursor-pointer text-[11px] font-semibold text-indigo-300">
          Messages ({body.request.messages.length})
        </summary>
        <pre className="bg-slate-950/60 border border-slate-800 rounded-md p-2 mt-1 overflow-x-auto whitespace-pre-wrap text-[11px] leading-relaxed">
          {JSON.stringify(body.request.messages, null, 2)}
        </pre>
      </details>
      <details
        data-testid="audit-llm-response"
        open
      >
        <summary className="cursor-pointer text-[11px] font-semibold text-indigo-300">
          Response
        </summary>
        <pre className="bg-slate-950/60 border border-slate-800 rounded-md p-2 mt-1 overflow-x-auto whitespace-pre-wrap text-[11px] leading-relaxed">
          {body.response.text}
        </pre>
      </details>
    </div>
  )
}

function VerifyBadge() {
  const verify = useAuditStore((s) => s.verify)
  if (!verify) {
    return (
      <span
        data-testid="audit-verify"
        data-state="unknown"
        className="text-xs text-slate-500"
      >
        verify pending
      </span>
    )
  }
  if (verify.ok) {
    return (
      <span
        data-testid="audit-verify"
        data-state="ok"
        className="alm-chip alm-chip-ok"
      >
        chain ok · {verify.length} entries
      </span>
    )
  }
  return (
    <span
      data-testid="audit-verify"
      data-state="bad"
      className="alm-chip alm-chip-bad"
    >
      chain broken · {verify.diag}
    </span>
  )
}

function PhaseGroupView({
  group,
  selectedTs,
  onSelect,
}: {
  group: AuditPhaseGroup
  selectedTs: string | null
  onSelect: (ts: string | null) => void
}) {
  return (
    <div
      data-testid={
        group.phase === null ? 'audit-group-unscoped' : `audit-group-${group.phase}`
      }
      className="mb-5"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-semibold text-indigo-300">
          {group.phase === null ? 'Cross-phase' : `Phase ${group.phase}`}
        </h2>
        <div className="text-[11px] text-slate-500">
          {group.events.length} event{group.events.length === 1 ? '' : 's'}
          {group.durationMs !== null ? (
            <> · {(group.durationMs / 1000).toFixed(1)}s</>
          ) : null}
        </div>
      </div>
      <ul className="space-y-1">
        {group.events.map((event) => (
          <EntryRow
            key={event.ts + ':' + event.event}
            event={event}
            isSelected={event.ts === selectedTs}
            onSelect={() => onSelect(event.ts === selectedTs ? null : event.ts)}
          />
        ))}
      </ul>
    </div>
  )
}

function EntryRow({
  event,
  isSelected,
  onSelect,
}: {
  event: AuditEvent
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        data-testid={`audit-entry-${event.event}`}
        data-selected={isSelected}
        className={`w-full text-left flex items-center gap-2 p-2 rounded-md text-xs ${
          isSelected
            ? 'bg-indigo-500/10 ring-1 ring-indigo-500/50'
            : 'hover:bg-white/5'
        }`}
      >
        <span className="text-[10px] text-slate-500 tabular-nums">
          {formatTs(event.ts)}
        </span>
        <span className="font-semibold text-slate-100">{event.event}</span>
        {event.agent ? (
          <span className="alm-chip text-[10px]">{event.agent}</span>
        ) : null}
        <span className="ml-auto text-[10px] text-slate-500 truncate max-w-[200px]">
          {summarisePayload(event)}
        </span>
      </button>
    </li>
  )
}

function SelectedEntry({ event }: { event: AuditEvent }) {
  return (
    <div className="text-xs">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {event.event}
      </div>
      <div className="text-sm font-semibold text-slate-100 mb-2">
        {event.agent ? `${event.agent} · ` : ''}
        {event.phase !== null ? `Phase ${event.phase}` : 'Cross-phase'}
      </div>
      <div className="text-[10px] text-slate-500 mb-3">{event.ts}</div>
      <pre
        data-testid="audit-detail-payload"
        className="bg-slate-950/60 border border-slate-800 rounded-md p-2 overflow-x-auto whitespace-pre-wrap text-[11px] leading-relaxed"
      >
        {JSON.stringify(event.payload, null, 2)}
      </pre>
      {event.event === 'llm_usage' ? (
        <LLMCallDetail payload={event.payload as unknown as LLMUsagePayload} />
      ) : null}
      {event.event === 'rewriter.proposed' ? (
        <RewriterDetail
          payload={event.payload as unknown as RewriterProposedPayload}
        />
      ) : null}
    </div>
  )
}

/**
 * Inline before/after diff for a ``rewriter.proposed`` event
 * (T7 — plan §7.4). Renders three things:
 *
 *   * Meta header — finding id + requirement id + rule + severity +
 *     status chip. Lets the architect cross-reference the diff to
 *     the auditor finding it patched without leaving the viewer.
 *   * The diff itself, line-by-line, color-coded.
 *   * A small summary (`+N / -M`) so a long diff's shape is legible
 *     before scrolling.
 *
 * The component intentionally avoids react-markdown / shiki — a
 * requirement statement is plain text in 99% of cases and the
 * heavyweight markdown renderer pulls in syntax-highlighting we
 * don't need here. If a payload ships markdown later we can add
 * the renderer behind the same testid without changing call sites.
 */
function RewriterDetail({ payload }: { payload: RewriterProposedPayload }) {
  const ops = useMemo(
    () => lineDiff(payload.before ?? '', payload.after ?? ''),
    [payload.before, payload.after],
  )
  const summary = useMemo(() => summariseDiff(ops), [ops])
  return (
    <div data-testid="audit-rewriter-detail" className="mt-3">
      <div
        data-testid="audit-rewriter-meta"
        className="text-[10px] uppercase tracking-wider text-slate-500 mb-1"
      >
        Rewriter proposal · {payload.finding_id}
      </div>
      <div className="text-[11px] text-slate-400 mb-2">
        <span className="font-mono">{payload.requirement_id}</span>
        {' · '}
        <span>{payload.rule}</span>
        {' · '}
        <span>{payload.severity}</span>
        {' · '}
        <span
          data-testid="audit-rewriter-status"
          data-status={payload.status}
          className="alm-chip text-[10px]"
        >
          {payload.status}
        </span>
      </div>
      <DiffBlock ops={ops} />
      <div
        data-testid="audit-rewriter-summary"
        className="text-[10px] text-slate-500 mt-1 font-mono"
      >
        +{summary.added} / -{summary.removed}
      </div>
      {payload.status_reason ? (
        <div
          data-testid="audit-rewriter-status-reason"
          className="text-[11px] text-slate-400 mt-1 italic"
        >
          {payload.status_reason}
        </div>
      ) : null}
    </div>
  )
}

function DiffBlock({ ops }: { ops: DiffOp[] }) {
  if (ops.length === 0) {
    return (
      <div
        data-testid="audit-rewriter-diff-empty"
        className="text-[11px] text-slate-500 italic"
      >
        before/after are both empty — no diff to render.
      </div>
    )
  }
  return (
    <pre
      data-testid="audit-rewriter-diff"
      className="bg-slate-950/60 border border-slate-800 rounded-md overflow-x-auto text-[11px] leading-relaxed font-mono"
    >
      {ops.map((op, idx) => (
        <DiffLine key={`${idx}:${op.kind}`} op={op} />
      ))}
    </pre>
  )
}

function DiffLine({ op }: { op: DiffOp }) {
  const cls =
    op.kind === 'add'
      ? 'text-emerald-300 bg-emerald-900/20'
      : op.kind === 'remove'
        ? 'text-rose-300 bg-rose-900/20'
        : 'text-slate-400'
  const sigil = op.kind === 'add' ? '+ ' : op.kind === 'remove' ? '- ' : '  '
  return (
    <div
      data-testid={`audit-rewriter-diff-${op.kind}`}
      className={`px-2 whitespace-pre-wrap ${cls}`}
    >
      {sigil}
      {op.text}
    </div>
  )
}

function formatTs(ts: string): string {
  // Parse defensively — a malformed line may slip through if a future
  // producer ships a non-ISO format. Falling back to the raw string
  // beats a JS exception in the timeline.
  const dt = new Date(ts)
  if (Number.isNaN(dt.getTime())) return ts
  return dt.toISOString().slice(11, 19)
}

function summarisePayload(event: AuditEvent): string {
  if (event.event === 'llm_usage') {
    const model = (event.payload as { model?: string }).model ?? '?'
    const input = (event.payload as { input_tokens?: number }).input_tokens ?? 0
    const output = (event.payload as { output_tokens?: number }).output_tokens ?? 0
    return `${model} · ${input}↘ ${output}↗`
  }
  if (event.event === 'phase_started' || event.event === 'phase_completed') {
    return ''
  }
  const keys = Object.keys(event.payload ?? {})
  if (keys.length === 0) return ''
  return keys.slice(0, 3).join(' · ')
}
